<template>
  <WithBoxSize v-slot="{ borderBoxSize }" :class="$style.content">
    <AspectRatio
      v-if="data?.fields?.poster?.fields?.file?.url"
      ratio="16x9"
      :class="$style.media"
      @click="
        onRequestPlayVideo(
          format === 'dynamic'
            ? borderBoxSize.width < (data?.fields?.dynamicBreakpoint || 0)
              ? 'modal'
              : undefined
            : format,
        )
      "
    >
      <BaseVideoIframe
        v-if="!shouldOpenModal && shouldPlayInline && computedIframeSrc"
        :src="computedIframeSrc"
        :class="$style.iframe"
      />
      <template v-if="!shouldPlayInline">
        <BasePicture
          :src="data?.fields?.poster?.fields?.file?.url"
          :width="data?.fields?.poster?.fields?.file?.details?.image?.width"
          :height="data?.fields?.poster?.fields?.file?.details?.image?.height"
          alt=""
          :img-class="$style.poster"
        />
        <div :class="$style.iconWrapper">
          <IconPlayRounded :class="$style.playIcon" />
        </div>
      </template>
    </AspectRatio>

    <BaseModal
      :is-open="shouldOpenModal"
      overlay-color="rgba(255, 255, 255, 0.8)"
      @request-close="onRequestCloseModal"
    >
      <BaseColumn
        v-if="shouldOpenModal"
        expand
        main-axis-alignment="center"
        cross-axis-alignment="center"
        @click="onRequestCloseModal"
      >
        <BaseColumn cross-axis-alignment="end" :class="$style.modalContent">
          <BaseButton
            :class="$style.closeBtn"
            :aria-label="localizedStrings['close-modal'] || 'Close Modal'"
            presentation="strip"
            @click.stop="onRequestCloseModal"
          >
            <IconTimes :class="$style.timesIcon" alt="" />
          </BaseButton>
          <BaseVideoIframe
            v-if="computedIframeSrc"
            :src="computedIframeSrc"
            :style="{
              '--theme-accent-color': 'transparent',
              '--theme-primary-color--200': 'var(--black-alpha75)',
            }"
            @click.stop
          />
        </BaseColumn>
      </BaseColumn>
    </BaseModal>
  </WithBoxSize>
</template>

<script setup lang="ts">
import type { TypeVideoContentWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { localizedStrings } = useLocalizedStrings()

const props = defineProps<{
  data: TypeVideoContentWithoutUnresolvableLinksResponse
}>()

const shouldOpenModal = ref(false)
const shouldPlayInline = ref(false)
const shouldPlay = ref(false)

const format = props.data?.fields?.format || 'modal'

const computedIframeSrc = computed(() => {
  if (props.data?.fields?.iframeSrc) {
    return (
      props.data?.fields?.iframeSrc +
      (props.data?.fields?.iframeSrc.includes('?') ? '&' : '?') +
      `autoplay=${shouldPlay.value ? 1 : 0}`
    )
  }
  return props.data?.fields?.media?.fields?.file?.url
})

const openModal = () => {
  shouldOpenModal.value = true
}

const onRequestPlayVideo = (
  format: TypeVideoContentWithoutUnresolvableLinksResponse['fields']['format'],
) => {
  shouldPlay.value = true
  if (format === 'modal') {
    openModal()
  } else {
    shouldPlayInline.value = true
  }
}

const onRequestCloseModal = () => {
  shouldOpenModal.value = false
  shouldPlay.value = false
}
</script>

<style lang="scss" module>
.media {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
}

.iframe {
  width: 100%;
  grid-column: 1 /1;
  grid-row: 1 / 1;
}

.poster {
  grid-column: 1 /1;
  grid-row: 1 / 1;
  cursor: pointer;
}

.iconWrapper {
  pointer-events: none;
  display: grid;
  place-content: center;
  filter: drop-shadow(0 0 10px rgba(black, 0.5));
  grid-column: 1 /1;
  grid-row: 1 / 1;
}

.playIcon {
  @include fluid-type(24px, 50px);
  width: 1em;
  height: 1em;
  color: var(--white);
}

.modalContent {
  position: relative;
}

.closeBtn {
  position: absolute;
  top: -40px;
  right: 0;
}

.timesIcon {
  color: var(--purple);
  width: 25px;
  height: 25px;
}
</style>
